import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { UnauthorizedModal } from 'widgets/roles-modal/ui/UnauthorizedModal/UnauthorizedModal';

import { useApplicationUserQuery, useChangeStatusMutation, useRequestFreeHelpMutation } from 'entities/application/api';
import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { RequestHelpFree } from 'features/application/install/ui/RequestHelpFree/RequestHelpFree';
import { ModalButtonStyled } from 'features/tariffs/subscribe/ui/SubscribeModals/SubscribeModalContainer/SubscribeModalContainer.styles';
import { baseUrl } from 'shared/constants';
import { isValidEmailAddress } from 'shared/lib/isValidEmailAddress';
import { logger } from 'shared/lib/logger/logger';
import { ModalFooter } from 'shared/ui/ModalFooter';
import { MpButton } from 'shared/ui/MpButton';

type SetupApplicationProps = {
	applicationId: string;
	handleInstall: () => void;
	redirectUrl: string;
	borderRadius: boolean;
	isLink: boolean;
	isDownloadable?: boolean;
	disabled?: boolean;
	hasSetupSupport?: boolean;
	applicationName?: string;
	hasAdditionalFlow?: FunctionComponent<{ isInstalled: boolean; handleInstall?: () => Promise<void> }>;
};
export const SetupApplication: React.FC<SetupApplicationProps> = ({
	applicationId,
	handleInstall,
	applicationName,
	isLink,
	redirectUrl,
	borderRadius,
	isDownloadable,
	disabled,
	hasSetupSupport,
	hasAdditionalFlow,
}) => {
	const { data: applicationUserInfo, refetch: refetchApplication } = useApplicationUserQuery(applicationId);
	const authorization = useAuthorization();
	const { t } = useTranslation();
	const [email, setEmail] = useState(``);
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

	const [changeStatus, { isLoading: isChangeStatusLoading, isSuccess: isChangeStatusSuccess }] =
		useChangeStatusMutation();

	const [sendRequest, { isLoading }] = useRequestFreeHelpMutation();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [blockRequest, setBlockRequest] = useState(true);

	const closeModal = (): void => {
		setIsModalOpen(false);
	};

	const openModal = (): void => {
		setIsModalOpen(true);
	};

	const openHelpModal = (): void => {
		setIsHelpModalOpen(true);
	};

	const closeHelpModal = (): void => {
		setIsHelpModalOpen(false);
		setEmail(``);
	};

	const unauthorizedFooter = [
		<ModalFooter key='1'>
			<span>
				<Button key='back' size='large' onClick={closeModal} block>
					{t(`Marketplace_Cancel`)}
				</Button>
			</span>
			<a
				key='login-link'
				href={`${baseUrl}/privateapi/auth/externallogin?provider=mpoidc&returnUrl=${window.location.href}`}
			>
				<Button key='login' type='primary' block>
					{t(`Marketplace_Sign_In_Dodo_IS`)}
				</Button>
			</a>
		</ModalFooter>,
	];

	const redirectToApplication = (): void => {
		window.location.href = redirectUrl;
	};

	useEffect(() => {
		if (isChangeStatusSuccess) {
			void refetchApplication();
		}
	}, [isChangeStatusSuccess, refetchApplication]);

	const handleSetup = async (): Promise<void> => {
		await changeStatus({
			id: applicationId,
			status: `Installed`,
		});
		logger.current.info(`User installed the application`, {
			userName: authorization?.login,
			userId: authorization?.userId,
			appName: applicationName,
			applicationId,
			eventId: 10106,
		});

		if (redirectUrl !== ``) {
			redirectToApplication();
		}
		handleInstall();
	};

	const updateEmail = (newEmail: string): void => {
		setEmail(newEmail);
	};

	const handleCheckboxChange = (e: CheckboxChangeEvent): void => {
		setBlockRequest(!e.target.checked);
	};

	const handleClick = async (): Promise<void> => {
		if (hasSetupSupport) {
			openHelpModal();
		} else {
			await handleSetup();
		}
	};

	const handleHelpRequest = async (): Promise<void> => {
		await sendRequest({ email });
		await changeStatus({
			id: applicationId,
			status: `Installed`,
		});
		handleInstall();
		closeHelpModal();
	};

	if (!authorization) {
		return (
			<>
				<Modal
					width={690}
					open={isModalOpen}
					onCancel={closeModal}
					className='roles-modal'
					footer={unauthorizedFooter}
				>
					<UnauthorizedModal />
				</Modal>
				<MpButton block size='large' onClick={openModal}>
					{t(`Marketplace_Sign_In_And_Connect`)}
				</MpButton>
			</>
		);
	}

	if ((!applicationUserInfo || applicationUserInfo.status === `Installed`) && !disabled) {
		return null;
	}

	if (applicationUserInfo?.status !== `Installed` && hasAdditionalFlow) {
		return <>{hasAdditionalFlow({ isInstalled: false, handleInstall: handleSetup })}</>;
	}

	return (
		// eslint-disable-next-line react/jsx-no-bind, @typescript-eslint/no-misused-promises, react/jsx-no-useless-fragment
		<>
			<MpButton
				size='large'
				loading={isChangeStatusLoading}
				type={isLink ? `default` : `primary`}
				onClick={handleClick}
				fullWidth
				style={borderRadius ? {} : { borderRadius: `0` }}
				disabled={disabled}
			>
				{isDownloadable ? t(`Marketplace_Download_Module`) : t(`Marketplace_Install`)}
			</MpButton>
			{hasSetupSupport && (
				<Modal
					open={isHelpModalOpen}
					onCancel={closeHelpModal}
					width={690}
					footer={[
						<ModalFooter key='footer'>
							<span>
								<a
									key='skip'
									href='https://app.konsol.pro/company/integrations'
									target='_blank'
									rel='noreferrer'
								>
									<ModalButtonStyled size='large' onClick={handleHelpRequest}>
										{t(`Marketplace_Already_Have_Access_To_Konsol`)}
									</ModalButtonStyled>
								</a>
							</span>
							<ModalButtonStyled
								key='leave-request'
								size='large'
								type='primary'
								loading={isLoading}
								onClick={handleHelpRequest}
								disabled={blockRequest || !isValidEmailAddress(email)}
							>
								{t(`Marketplace_Leave_Request_Konsol`)}
							</ModalButtonStyled>
						</ModalFooter>,
					]}
				>
					<RequestHelpFree
						email={email}
						updateEmail={updateEmail}
						handleCheckboxChange={handleCheckboxChange}
					/>
				</Modal>
			)}
		</>
	);
};
